import { useEffect, FC, useReducer, useState } from 'react';
import {
  Button,
  ButtonStyles,
  ButtonSize,
  ButtonTypes,
  Drawer,
  Input,
  PhoneNumber,
  InputTypes,
  useTranslation,
  MLPVersion,
  DrawerDirections,
  DropDownValidations,
  SmartInput,
  InputType,
  InputValidations,
} from '@nextgen-web-framework/all';
import { DataTypesEnums, getValueCheck } from '@commerce-checkout-webcomponents/sdk';
import {
  shippingDetailsPickupContactDrawerInitialState,
  shippingDetailsPickupContactDrawerReducer,
} from '../../common/helpers/CheckoutShippingHelpers';
import { parseContactNumber } from '../../../utils/CheckoutUtils';
import { checkoutShippingDetailsActions, countryDropdownLabelField } from '../../common/config/checkoutConstants';
import { ICheckoutPickupContactDrawerProps } from '../types/propTypes';
import { usePhoneNumberData } from '../../common/custom-hooks/usePhoneNumberData';
import { isEmail } from '../../common/transformer';
import styles from './CheckoutPickupContactDrawer.module.scss';

export const CheckoutPickupContactDrawer: FC<ICheckoutPickupContactDrawerProps> = ({
  onCloseDrawer,
  bopisDetails,
  confirmButtonLoading,
  onConfrimPickupContact,
}: ICheckoutPickupContactDrawerProps) => {
  const t = useTranslation();
  const countryMasterData = usePhoneNumberData();
  const getPhoneNumber = (contact: string) => parseContactNumber(contact, false);
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState<boolean>(true);
  const requiredFieldsvalidation = {
    recipientName: false,
    recipientContact: false,
  };
  const [fieldSvalidation, setFieldSvalidation] = useState(requiredFieldsvalidation);
  const [emailValidationStatus, setEmailValidationStatus] = useState(InputValidations.DEFAULT);
  const pickupContactNumber = getPhoneNumber(
    bopisDetails?.countryCode && bopisDetails?.mobileNo
      ? bopisDetails.countryCode + bopisDetails.mobileNo
      : getValueCheck(bopisDetails?.mobileNo, DataTypesEnums.TYPE_STRING),
  ).phone;
  const [state, dispatchStateAction] = useReducer(shippingDetailsPickupContactDrawerReducer, {
    ...shippingDetailsPickupContactDrawerInitialState,
    recipientContact: pickupContactNumber,
    recipientCountryCode:
      getPhoneNumber(
        bopisDetails?.countryCode && bopisDetails?.mobileNo
          ? bopisDetails.countryCode + bopisDetails.mobileNo
          : getValueCheck(bopisDetails?.mobileNo, DataTypesEnums.TYPE_STRING),
      ).countryCode ||
      countryMasterData.defaultCountryCode ||
      bopisDetails?.countryCode,
    recipientName: getValueCheck(bopisDetails?.name, DataTypesEnums.TYPE_STRING),
    recipientEmail: getValueCheck(bopisDetails?.email, DataTypesEnums.TYPE_STRING),
  });

  useEffect(() => {
    const email = state.recipientEmail;
    const status = !email
      ? InputValidations.DEFAULT
      : isEmail(email)
      ? InputValidations.VALID
      : InputValidations.INVALID;
    setEmailValidationStatus(status);
  }, [state.recipientEmail]);

  const fieldBlurHandler = (fieldName: string, value?: string) => {
    const fieldsStatus = { ...fieldSvalidation };
    setFieldSvalidation({
      ...fieldsStatus,
      [fieldName]: !state[fieldName] && !value ? true : false,
    });
  };

  return (
    <div className={styles['checkout-pickup-details-drawer-container']}>
      <Drawer
        header={t('checkout.CheckoutPickupContactDrawer.title')}
        closeOnBackdropClick={true}
        closeIcon={true}
        onClose={() => onCloseDrawer()}
        bottomGap={0}
        direction={DrawerDirections.FULLHEIGHT}
        footer={
          <div className={styles['checkout-pickup-contact-form-btn']}>
            <Button
              title={!confirmButtonLoading ? t('checkout.CheckoutPickupDrawer.confirmButtonText') : undefined}
              type={ButtonTypes.SUBMIT}
              buttonStyle={ButtonStyles.SOLID}
              size={ButtonSize.MEDIUM}
              onClick={() =>
                onConfrimPickupContact({
                  countryCode: state.recipientCountryCode,
                  mobileNo: state.recipientContact,
                  name: state.recipientName,
                  email: state.recipientEmail,
                })
              }
              disabled={
                !state.recipientContact ||
                !state.recipientCountryCode ||
                !isValidPhoneNumber ||
                !state.recipientName ||
                emailValidationStatus == InputValidations.INVALID
              }
            />
          </div>
        }
        className={styles['checkout-pickup-details-drawer-container']}
        useReactPortal={true}
        portalContainerId={'react-portal-drawer'}
      >
        <div className={styles['checkout-pickup-details-drawer-body']}>
          <div className={styles['pickup-contactName-field']}>
            <Input
              id={styles['pickup-name']}
              data-testid="pickup-name"
              name="pickupContactName"
              value={state.recipientName}
              onChange={(val) => {
                dispatchStateAction({
                  type: checkoutShippingDetailsActions.SET_RECIPIENT_NAME,
                  payload: val.target.value || ('' as string),
                });
                fieldBlurHandler('recipientName', val.target.value);
              }}
              mlpVersion={MLPVersion.TWO}
              type={InputTypes.TEXT}
              label={t('checkout.CheckoutPickupContactDrawer.formLabelName')}
              onBlur={() => fieldBlurHandler('recipientName')}
              {...(fieldSvalidation.recipientName && {
                status: InputValidations.INVALID,
                message: t('checkout.CheckoutPickupContactDrawer.errorMsgName'),
              })}
            />
          </div>
          <div className={styles['pickup-contactNumber-field']}>
            <PhoneNumber
              dropdownDisabled={true}
              name="Recipients Contact"
              inputValue={state.recipientContact}
              configData={{
                label: countryDropdownLabelField,
                onSelect: (e) =>
                  dispatchStateAction({
                    type: checkoutShippingDetailsActions.SET_RECIPIENT_COUNTRY_CODE,
                    payload: e.callingCode as string,
                  }),
              }}
              isAutoValidate={true}
              inputLabel={t('checkout.CheckoutPickupContactDrawer.formLabelPhoneNumber')}
              dropdownLabel={t('checkout.CheckoutPickupDrawer.country')}
              hideDropdown={true}
              onInputChange={(val: unknown, status?: string) => {
                status == DropDownValidations.INVALID && setIsValidPhoneNumber(false);
                status == DropDownValidations.VALID && setIsValidPhoneNumber(true);
                dispatchStateAction({
                  type: checkoutShippingDetailsActions.SET_RECIPIENT_CONTACT,
                  payload: val as string,
                });
                fieldBlurHandler('recipientContact', val as string);
              }}
              dropdownValue={state.recipientCountryCode || countryMasterData.defaultCountryCode}
              data={countryMasterData.data}
              disabledFilter
              type="number"
              isPhoneNumberValid={(isValid: boolean) => {
                setIsValidPhoneNumber(isValid);
                return isValid;
              }}
              onBlur={() => fieldBlurHandler('recipientContact')}
              {...((fieldSvalidation.recipientContact || !isValidPhoneNumber) && {
                status: InputValidations.INVALID,
                message: t('checkout.CheckoutPickupContactDrawer.errorMsgPhoneNumber'),
              })}
            />
          </div>
          <div className={styles['email-field']}>
            <SmartInput
              name="pickup-email-field"
              smartInputType={InputType.EMAIL}
              inputValue={state.recipientEmail}
              onChange={(e) => {
                dispatchStateAction({
                  type: checkoutShippingDetailsActions.SET_RECIPIENT_EMAIL,
                  payload: e.target.value,
                });
              }}
              status={emailValidationStatus}
              emailLabel={t('checkout.CheckoutPickupContactDrawer.formLabelEmail')}
            />
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default CheckoutPickupContactDrawer;
